import { useId, useState } from 'react';
import { toast } from 'sonner';
import { Textarea } from '~/components/ui/textarea';
import { useCodingDesign } from '~/hooks/queries/use-coding-sessions';
import { useUpdateCodingDesign } from '~/hooks/mutations/use-coding-mutations';
import { rawFileUrl } from '~/features/chat/image-source';
import { DesignDirectiveChips, DesignReferenceThumb } from './design-direction-section';
import { DesignSuggestionViewer, type DesignSuggestionViewerItem } from './design-suggestion-viewer';
import { MAX_DESIGN_INSPIRATION_CHARS, type DesignDirective, type DesignReference } from './design-direction';

/**
 * The same src-resolution rule `DesignReferenceThumb`'s callers already
 * applied inline (an upload reads its file by id, a web suggestion carries
 * its own url) — pulled out so the zoomed viewer (feedback round 2) and the
 * thumbnail row agree on it by construction rather than by two copies
 * staying in sync.
 */
function designReferenceSrc(ref: DesignReference): string | undefined {
  if (ref.kind !== 'upload') return ref.imageUrl;
  if (!ref.fileId) return undefined;
  return rawFileUrl(ref.fileId);
}

/**
 * FRONT-2382 review: the Project-context dialog's "Design direction"
 * editor — its own query, draft state and save/discard wiring — pulled out
 * of `ProjectContextDialog` into this hook so that dialog composes it
 * rather than owning it inline. Unchanged from the original inline
 * version: same query, same dirty rule, same save payload, same toasts.
 *
 * The dialog still calls `dirty`/`saving` synchronously off this hook's
 * return value (not a lifted callback/effect) because the dialog's own
 * markup tests render through `renderToStaticMarkup`, which never runs
 * effects — a dirty flag that only updated via `useEffect` would never
 * reach the shared ActionBar in that renderer. `save()`/`discard()` are
 * plain functions the dialog's own handleSave/onDiscard call directly, on
 * the SAME click that drives the context half — matching the "both calls
 * when both dirty" rule the dialog's docblock describes.
 */
export function useDesignDirectionEditor(sessionId: string, enabled: boolean) {
  const inspirationId = useId();
  // Feedback round 2: which staged reference (if any) is open large right
  // now — the same zoomed viewer the New-project form's suggestion cards
  // use, here with a single item and no arrows/pick button.
  const [viewerRef, setViewerRef] = useState<DesignReference | null>(null);
  const { data: designRead, isLoading, isError, error } = useCodingDesign(sessionId, enabled);
  const designSaved = designRead?.design ?? null;
  const [designDraft, setDesignDraft] = useState<{
    inspiration: string;
    directives: DesignDirective[];
    removedReferenceIds: string[];
  } | null>(null);
  const updateDesign = useUpdateCodingDesign();

  const inspiration = designDraft?.inspiration ?? designSaved?.inspiration ?? '';
  const directives = designDraft?.directives ?? designSaved?.directives ?? [];
  const removedReferenceIds = designDraft?.removedReferenceIds ?? [];
  // What still shows in the list — a removal is a DRAFT edit like the two
  // fields above it, reversible via the same Revert until Save commits it.
  const references = (designSaved?.references ?? []).filter((r) => !removedReferenceIds.includes(r.id));

  function patch(
    p: Partial<{ inspiration: string; directives: DesignDirective[]; removedReferenceIds: string[] }>,
  ) {
    setDesignDraft({ inspiration, directives, removedReferenceIds, ...p });
  }

  const dirty =
    enabled &&
    designRead !== undefined &&
    (inspiration.trim() !== (designSaved?.inspiration ?? '').trim() ||
      // Set-equality, not array-equality — a chip toggled off then back on
      // must not read as dirty just because the order changed.
      [...directives].sort().join(',') !== [...(designSaved?.directives ?? [])].sort().join(',') ||
      removedReferenceIds.length > 0);

  // FRONT-2382 review #12: a design-only save (context not dirty) used to
  // complete with no feedback at all — the ActionBar just closes, which
  // reads as nothing happened. Fires independently of the context save's
  // own toast, matching the dialog's "both calls when both dirty" rule: if
  // BOTH halves save at once, both report their own completion rather than
  // one swallowing the other's.
  function save() {
    if (!dirty) return;
    updateDesign.mutate(
      {
        sessionId,
        inspiration: inspiration.trim(),
        directives,
        ...(removedReferenceIds.length > 0 ? { removeReferenceIds: removedReferenceIds } : {}),
      },
      {
        onSuccess: () => {
          setDesignDraft(null);
          toast.success('Design direction saved — EDDY uses it from his next run');
        },
        onError: (err) => toast.error((err as Error).message || 'Could not save the design direction'),
      },
    );
  }

  function discard() {
    setDesignDraft(null);
  }

  return {
    inspirationId,
    isLoading,
    isError,
    error,
    inspiration,
    directives,
    references,
    onInspirationChange: (next: string) => patch({ inspiration: next }),
    onToggleDirective: (d: DesignDirective) =>
      patch({ directives: directives.includes(d) ? directives.filter((x) => x !== d) : [...directives, d] }),
    onRemoveReference: (id: string) => patch({ removedReferenceIds: [...removedReferenceIds, id] }),
    pending: updateDesign.isPending,
    dirty,
    save,
    discard,
    viewerRef,
    onView: (ref: DesignReference) => setViewerRef(ref),
    onCloseViewer: () => setViewerRef(null),
  };
}

/**
 * The rendered "Design direction" block — inspiration textarea, directive
 * chips, staged reference list — driven entirely by {@link useDesignDirectionEditor}'s
 * return value. Own component (FRONT-2382 review) so `ProjectContextDialog`
 * composes it rather than rendering ~100 lines of this inline.
 */
export function DesignDirectionEditor({ editor }: { editor: ReturnType<typeof useDesignDirectionEditor> }) {
  return (
    <div className="border-t border-border pt-4">
      <h3 className="text-[length:var(--text-sm)] font-medium text-foreground" style={{ marginBottom: 10 }}>
        Design direction
      </h3>
      {editor.isLoading ? (
        <p className="text-[length:var(--text-sm)] text-muted-foreground" style={{ margin: 0 }}>
          Loading…
        </p>
      ) : editor.isError ? (
        <p className="text-[length:var(--text-sm)] text-destructive [text-wrap:pretty]" style={{ margin: 0 }}>
          Could not read the design direction: {(editor.error as Error)?.message ?? 'unknown error'}
        </p>
      ) : (
        <div className="space-y-4">
          <div>
            <label
              htmlFor={editor.inspirationId}
              className="block text-[length:var(--text-xs)] font-medium text-muted-foreground"
              style={{ marginBottom: 6 }}
            >
              Inspiration & references
            </label>
            <Textarea
              id={editor.inspirationId}
              value={editor.inspiration}
              onChange={(e) => editor.onInspirationChange(e.target.value)}
              maxLength={MAX_DESIGN_INSPIRATION_CHARS}
              rows={3}
              placeholder="Sites, products or styles you like…"
              disabled={editor.pending}
              className="resize-none rounded-md shadow-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
            />
          </div>
          <DesignDirectiveChips
            selected={editor.directives}
            onToggle={editor.onToggleDirective}
            disabled={editor.pending}
          />
          {editor.references.length > 0 && (
            <div className="space-y-2">
              {editor.references.map((ref) => {
                const src = designReferenceSrc(ref);
                return (
                  <div
                    key={ref.id}
                    className="flex min-w-0 items-center gap-2 rounded-md border border-border p-2"
                  >
                    <DesignReferenceThumb
                      src={src}
                      alt={ref.title}
                      // Feedback round 2: the same zoomed viewer the
                      // suggestion cards use, single image, no arrows,
                      // no pick button (only `pick` on the viewer
                      // toggles that, and it's omitted here).
                      onView={src ? () => editor.onView(ref) : undefined}
                      viewLabel={`View ${ref.title} large`}
                      onRemove={() => editor.onRemoveReference(ref.id)}
                      removeLabel={`Remove ${ref.title}`}
                      testId="design-dialog-reference"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[length:var(--text-sm)] text-foreground" style={{ margin: 0 }}>
                        {ref.title}
                      </p>
                      {ref.description && (
                        // Feedback round 2 (live finding): this used to
                        // be `truncate` (single line, no wrap) — a long
                        // description then forced its own min-content
                        // width past the row's available space, which
                        // is exactly what inflated the dialog past its
                        // card (see the note on `DialogContent` in
                        // `project-context-dialog.tsx`). Clamping to 2
                        // lines instead lets it wrap within the space
                        // it's given rather than demanding more.
                        <p
                          className="line-clamp-2 text-[length:var(--text-xs)] text-muted-foreground"
                          style={{ margin: 0 }}
                        >
                          {ref.description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The shared zoomed viewer for a staged reference (feedback round 2), a
 * sibling of {@link DesignDirectionEditor} rather than nested inside it —
 * `ProjectContextDialog` renders it OUTSIDE the dialog's scrolling region
 * (same position the inline version used), so it never clips against
 * `overflow-y-auto`. Renders nothing while no reference is open.
 */
export function DesignDirectionEditorViewer({
  reference,
  onClose,
}: {
  reference: DesignReference | null;
  onClose: () => void;
}) {
  if (!reference) return null;
  return (
    <DesignSuggestionViewer
      items={[
        {
          id: reference.id,
          title: reference.title,
          description: reference.description,
          imageUrl: designReferenceSrc(reference) ?? '',
        } satisfies DesignSuggestionViewerItem,
      ]}
      index={0}
      onIndexChange={() => {}}
      onClose={onClose}
    />
  );
}
